import type { Project, ProjectEvent, ProjectPhase, ProjectTask, SubTask, TaskStatus } from '../types';
import { diffProjectActivity } from '../utils/projectActivity';

/** All values are sampled by the caller, including the mutable XP configuration. */
export interface ProjectReducerContext {
  now: string;
  newId: string;
  userLevel: number;
  mint: 'mint' | 'defer';
  points: { project: number; task: number; subTask: number };
}
export interface MintIntent {
  activityName: string;
  domainId: string;
  xpEarned: number;
  completedAt: string;
  metadata: { projectId: string; projectName: string; projectCompleted?: boolean; taskId?: string; subTaskId?: string; isSubTask?: boolean };
}
export interface ProjectReduction {
  project: Project;
  events: Omit<ProjectEvent, 'id'>[];
  mints: MintIntent[];
  warnings: string[];
}
export type ProjectReducer<A> = (project: Project, args: A, ctx: ProjectReducerContext) => ProjectReduction;

function record(project: Project, kind: keyof ProjectReducerContext['points'], ctx: ProjectReducerContext) {
  if (ctx.mint === 'defer') return undefined;
  const multiplier = project.complexity === 'high' ? 1.5 : project.complexity === 'medium' ? 1.2 : 1;
  return { points: Math.round(ctx.points[kind] * multiplier), userLevel: ctx.userLevel, earnedAt: ctx.now };
}
export function calculateProjectProgress(_phases: ProjectPhase[], tasks: ProjectTask[]): number {
  return tasks.length === 0 ? 0 : Math.round(tasks.filter(task => task.status === 'done').length / tasks.length * 100);
}
function reopen(status: Project['status'], progress: number): Project['status'] {
  // Reopening keeps paid records. Archived is a filing decision, never reopened.
  return status === 'completed' && progress < 100 ? 'active' : status;
}
function result(before: Project, project: Project, ctx: ProjectReducerContext, journal = false, history = true, warnings: string[] = []): ProjectReduction {
  const events = history ? diffProjectActivity(before, project, ctx.now, () => '').map(({ id, ...event }) => { void id; return event; }) : [];
  const mints: MintIntent[] = [];
  const metadata = { projectId: project.id, projectName: project.title };
  const add = (activityName: string, points: number, extra: Partial<MintIntent['metadata']>) => {
    mints.push({ activityName, domainId: project.category || '', xpEarned: points, completedAt: ctx.now, metadata: { ...metadata, ...extra } });
  };
  if (journal) {
    const old = before.id === project.id ? before : undefined;
    if (project.pointRecord && !old?.pointRecord) add(project.title, project.pointRecord.points, { projectCompleted: true });
    for (const task of project.tasks ?? []) {
      const previous = old?.tasks.find(t => t.id === task.id);
      if (task.pointRecord && !previous?.pointRecord) add(task.title, task.pointRecord.points, { taskId: task.id });
      for (const sub of task.subTasks ?? []) {
        if (sub.pointRecord && !previous?.subTasks?.find(s => s.id === sub.id)?.pointRecord) {
          add(`${task.title} - ${sub.title}`, sub.pointRecord.points, { taskId: task.id, subTaskId: sub.id, isSubTask: true });
        }
      }
    }
  }
  return { project, events, mints, warnings };
}
function withTasks(project: Project, tasks: ProjectTask[], ctx: ProjectReducerContext): Project {
  const progress = calculateProjectProgress(project.phases, tasks);
  let status = project.status;
  let pointRecord = project.pointRecord;
  if (progress === 100 && status === 'active') {
    status = 'completed';
    if (!pointRecord) pointRecord = record(project, 'project', ctx);
  }
  return { ...project, tasks, progress, status: reopen(status, progress), pointRecord, updatedAt: ctx.now };
}

export const updateProject: ProjectReducer<Partial<Project>> = (project, updates, ctx) => {
  let pointRecord = project.pointRecord;
  if (updates.status === 'completed' && project.status !== 'completed' && !pointRecord) pointRecord = record(project, 'project', ctx);
  return result(project, { ...project, ...updates, pointRecord, updatedAt: ctx.now }, ctx, true);
};
export const archiveProject: ProjectReducer<void> = (project, _args, ctx) => result(project, { ...project, status: 'archived', updatedAt: ctx.now }, ctx);
export const restoreProject: ProjectReducer<void> = (project, _args, ctx) => result(project, { ...project, status: 'active', updatedAt: ctx.now }, ctx);
export const addPhase: ProjectReducer<Omit<ProjectPhase, 'id' | 'order'>> = (project, phase, ctx) => result(project, {
  ...project, phases: [...project.phases, { ...phase, id: ctx.newId, order: project.phases.length, collapsed: false }], updatedAt: ctx.now,
}, ctx);
export const updatePhase: ProjectReducer<{ phaseId: string; updates: Partial<ProjectPhase> }> = (project, { phaseId, updates }, ctx) => result(project, {
  ...project, phases: project.phases.map(phase => phase.id === phaseId ? { ...phase, ...updates } : phase), updatedAt: ctx.now,
}, ctx);
export const deletePhase: ProjectReducer<{ phaseId: string }> = (project, { phaseId }, ctx) => {
  if (project.tasks.some(task => task.phaseId === phaseId)) return result(project, project, ctx, false, true, ['Cannot delete phase with tasks. Move or delete tasks first.']);
  return result(project, { ...project, phases: project.phases.filter(phase => phase.id !== phaseId).map((phase, order) => ({ ...phase, order })), updatedAt: ctx.now }, ctx);
};
export const reorderPhases: ProjectReducer<{ sourceIndex: number; targetIndex: number }> = (project, { sourceIndex, targetIndex }, ctx) => {
  // An index outside the list used to splice in `undefined`, which became a phase
  // holding only `{ order }`. Out of range is a no-op, as for reorderTasks.
  const inRange = (index: number) => Number.isInteger(index) && index >= 0 && index < project.phases.length;
  if (!inRange(sourceIndex) || !inRange(targetIndex)) return result(project, project, ctx, false, false);
  const phases = [...project.phases];
  const [removed] = phases.splice(sourceIndex, 1);
  phases.splice(targetIndex, 0, removed);
  return result(project, { ...project, phases: phases.map((phase, order) => ({ ...phase, order })), updatedAt: ctx.now }, ctx, false, false);
};
export const togglePhaseCollapse: ProjectReducer<{ phaseId: string }> = (project, { phaseId }, ctx) => result(project, {
  ...project, phases: project.phases.map(phase => phase.id === phaseId ? { ...phase, collapsed: !phase.collapsed } : phase),
}, ctx, false, false);

export const addTask: ProjectReducer<Omit<ProjectTask, 'id' | 'order'>> = (project, data, ctx) => {
  const task: ProjectTask = { ...data, id: ctx.newId, order: project.tasks.filter(t => t.phaseId === data.phaseId && t.status === data.status).length, subTasks: data.subTasks || [] };
  if (data.status === 'done') {
    task.completed = true;
    task.pointRecord = record(project, 'task', ctx);
  }
  const tasks = [...project.tasks, task];
  // Adding unfinished work never auto-completes or creates a project record.
  const progress = calculateProjectProgress(project.phases, tasks);
  const next = data.status === 'done' ? withTasks(project, tasks, ctx) : { ...project, tasks, progress, status: reopen(project.status, progress), updatedAt: ctx.now };
  return result(project, next, ctx, data.status === 'done');
};
export const updateTask: ProjectReducer<{ taskId: string; updates: Partial<ProjectTask> }> = (project, { taskId, updates }, ctx) => result(project,
  withTasks(project, project.tasks.map(task => task.id === taskId ? { ...task, ...updates } : task), ctx), ctx, true);
export const setTaskCompleted: ProjectReducer<{ taskId: string; completed: boolean }> = (project, { taskId, completed }, ctx) => {
  const existing = project.tasks.find(task => task.id === taskId);
  if (existing?.completed === completed && existing.status === (completed ? 'done' : 'todo')) return result(project, project, ctx);
  const tasks = project.tasks.map(task => task.id !== taskId ? task : {
    ...task, completed, status: completed ? 'done' as const : 'todo' as const,
    pointRecord: task.pointRecord ?? (completed ? record(project, 'task', ctx) : undefined),
  });
  return result(project, withTasks(project, tasks, ctx), ctx, true);
};
export const toggleTask: ProjectReducer<{ taskId: string }> = (project, { taskId }, ctx) => setTaskCompleted(project, {
  taskId, completed: !project.tasks.find(task => task.id === taskId)?.completed,
}, ctx);
function moveTo(project: Project, taskId: string, phaseId: string, status: TaskStatus, ctx: ProjectReducerContext, move: boolean): ProjectReduction {
  if (!project.tasks.some(task => task.id === taskId)) return result(project, project, ctx);
  const order = project.tasks.filter(task => task.phaseId === phaseId && task.status === status && task.id !== taskId).length;
  const tasks = project.tasks.map(task => task.id !== taskId ? task : {
    ...task, ...(move ? { phaseId } : {}), status, order, completed: status === 'done',
    pointRecord: task.pointRecord ?? (status === 'done' && !task.completed ? record(project, 'task', ctx) : undefined),
  });
  return result(project, withTasks(project, tasks, ctx), ctx, true);
}
export const updateTaskStatus: ProjectReducer<{ taskId: string; status: TaskStatus }> = (project, { taskId, status }, ctx) =>
  moveTo(project, taskId, project.tasks.find(task => task.id === taskId)?.phaseId ?? '', status, ctx, false);
export const moveTask: ProjectReducer<{ taskId: string; phaseId: string; status: TaskStatus }> = (project, { taskId, phaseId, status }, ctx) => moveTo(project, taskId, phaseId, status, ctx, true);
export const reorderTasks: ProjectReducer<{ phaseId: string; status: TaskStatus; sourceIndex: number; targetIndex: number }> = (project, { phaseId, status, sourceIndex, targetIndex }, ctx) => {
  const bucket = project.tasks.filter(task => task.phaseId === phaseId && task.status === status).sort((a, b) => a.order - b.order);
  if (sourceIndex < 0 || sourceIndex >= bucket.length || targetIndex < 0 || targetIndex >= bucket.length) return result(project, project, ctx);
  const [moved] = bucket.splice(sourceIndex, 1);
  bucket.splice(targetIndex, 0, moved);
  const ordered = bucket.map((task, order) => ({ ...task, order }));
  return result(project, { ...project, tasks: project.tasks.map(task => ordered.find(t => t.id === task.id) || task), updatedAt: ctx.now }, ctx);
};
export const deleteTask: ProjectReducer<{ taskId: string }> = (project, { taskId }, ctx) => {
  const tasks = project.tasks.filter(task => task.id !== taskId);
  const progress = calculateProjectProgress(project.phases, tasks);
  return result(project, { ...project, tasks, progress, status: reopen(project.status, progress), pointRecord: project.pointRecord, updatedAt: ctx.now }, ctx);
};
export const updateSubTask: ProjectReducer<{ taskId: string; subTaskId: string; updates: Pick<SubTask, 'title'> }> = (project, { taskId, subTaskId, updates }, ctx) => {
  const title = updates.title.trim();
  const sub = project.tasks.find(task => task.id === taskId)?.subTasks?.find(s => s.id === subTaskId);
  if (!title || !sub || sub.title === title) return result(project, project, ctx);
  return result(project, { ...project, updatedAt: ctx.now, tasks: project.tasks.map(task => task.id !== taskId ? task : {
    ...task, subTasks: task.subTasks?.map(s => s.id === subTaskId ? { ...s, title } : s),
  }) }, ctx);
};
export const addSubTask: ProjectReducer<{ taskId: string; title: string }> = (project, { taskId, title }, ctx) => result(project, {
  ...project, tasks: project.tasks.map(task => task.id !== taskId ? task : { ...task, subTasks: [...(task.subTasks || []), { id: ctx.newId, title: title.trim(), completed: false }] }), updatedAt: ctx.now,
}, ctx);
export const setSubTaskCompleted: ProjectReducer<{ taskId: string; subTaskId: string; completed: boolean }> = (project, { taskId, subTaskId, completed }, ctx) => {
  const existing = project.tasks.find(task => task.id === taskId)?.subTasks?.find(sub => sub.id === subTaskId);
  if (existing?.completed === completed) return result(project, project, ctx);
  const tasks = project.tasks.map(task => {
    if (task.id !== taskId) return task;
    const subTasks = (task.subTasks || []).map(sub => sub.id !== subTaskId ? sub : {
      ...sub, completed, pointRecord: sub.pointRecord ?? (completed ? record(project, 'subTask', ctx) : undefined),
    });
    const allDone = subTasks.every(sub => sub.completed);
    let taskCompleted = task.completed;
    let pointRecord = task.pointRecord;
    let status = task.status;
    if (allDone && !taskCompleted) {
      taskCompleted = true; status = 'done';
      if (!pointRecord) pointRecord = record(project, 'task', ctx);
    } else if (!allDone && taskCompleted) {
      taskCompleted = false; status = 'in-progress';
    }
    return { ...task, completed: taskCompleted, status, pointRecord, subTasks };
  });
  return result(project, withTasks(project, tasks, ctx), ctx, true);
};
export const toggleSubTask: ProjectReducer<{ taskId: string; subTaskId: string }> = (project, { taskId, subTaskId }, ctx) => setSubTaskCompleted(project, {
  taskId, subTaskId, completed: !project.tasks.find(task => task.id === taskId)?.subTasks?.find(sub => sub.id === subTaskId)?.completed,
}, ctx);
export const deleteSubTask: ProjectReducer<{ taskId: string; subTaskId: string }> = (project, { taskId, subTaskId }, ctx) => result(project, {
  ...project, tasks: project.tasks.map(task => task.id !== taskId ? task : { ...task, subTasks: (task.subTasks || []).filter(sub => sub.id !== subTaskId) }), updatedAt: ctx.now,
}, ctx);
export const convertTaskToProject: ProjectReducer<{ taskId: string; newProjectId: string }> = (project, { taskId, newProjectId }, ctx) => result(project, {
  ...project, tasks: project.tasks.map(task => task.id !== taskId ? task : { ...task, linkedProjectId: newProjectId }), updatedAt: ctx.now,
}, ctx);
export const moveProjectToFolder: ProjectReducer<{ folderId: string | undefined }> = (project, { folderId }, ctx) => result(project, { ...project, folderId, updatedAt: ctx.now }, ctx, false, false);
export const moveProjectToWorkspace: ProjectReducer<{ workspaceId: string | undefined }> = (project, { workspaceId }, ctx) => result(project, { ...project, workspaceId, updatedAt: ctx.now }, ctx, false, false);
export const clearProjectFolder: ProjectReducer<void> = (project, _args, ctx) => result(project, { ...project, folderId: undefined }, ctx, false, false);
export const clearProjectWorkspace: ProjectReducer<void> = (project, _args, ctx) => result(project, { ...project, workspaceId: undefined }, ctx, false, false);
